// @ts-nocheck
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import {
  getPortServiceCategoryLabel,
  STATUS_KEYS,
  STATUS_MAP,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import {
  type LinkedVesselCalls,
  type PortServicesFilterPayload,
} from "@services/port-services/types";

export type StatusOption = { label: string; value: string };

const NOC_STATUS_FILTERS = {
  ISSUED: {
    label: "NOC Issued",
    value: "NOC_ISSUED",
  },
  PENDING: {
    label: "NOC Pending",
    value: "NOC_PENDING",
  },
} as const satisfies Record<string, StatusOption>;

export const PORT_SERVICES_FIELDS = Object.freeze({
  id: "id",
  serviceRequestNumber: "ID",
  vesselCallNo: "Vessel Call No",
  imo: "IMO",
  vesselName: "Vessel Name",
  serviceCategory: "Service Category",
  serviceType: "Service Type",
  serviceSubType: "Service Sub-type",
  rts: "RTS",
  ats: "ATS",
  atc: "ATC",
  status: "Status",
});

const getExistingList = (portServices = [], propName: string) => {
  return [...new Set(portServices.map((ps) => ps[propName]))]
    .filter((ps) => ps)
    .map((ps) => ({
      value: ps,
      label: propName === "status" ? STATUS_MAP[ps].label : ps,
    }));
};

const getStatusFilterOptions = (): StatusOption[] => {
  const optionsByLabel = new Map<string, StatusOption>();

  Object.entries(STATUS_MAP).forEach(([statusKey, statusInfo]) => {
    const option =
      statusKey === STATUS_KEYS.HSE_APPROVED
        ? NOC_STATUS_FILTERS.ISSUED
        : { label: statusInfo.label, value: statusKey };

    if (!optionsByLabel.has(option.label)) {
      optionsByLabel.set(option.label, option);
    }
  });

  return [...Array.from(optionsByLabel.values()), NOC_STATUS_FILTERS.PENDING];
};

export const getMainVessel = (vessels?: LinkedVesselCalls[]) => {
  return vessels?.find((v) => v.role === "MAIN");
};

export const getSecondaryVessel = (vessels?: LinkedVesselCalls[]) => {
  return vessels?.find((v) => v.role === "SECONDARY");
};

export const getPortServicesFilterFields = (
  portServiceCategories,
  portServiceTypes,
  portServiceSubtypes,
  isPage,
): Record<string, FilterFieldDefinition> => {
  const extraFieldsForPage = {};
  if (isPage) {
    extraFieldsForPage["vesselCallNo"] = {
      label: PORT_SERVICES_FIELDS.vesselCallNo,
      type: "integer",
      minLength: 3,
    };
    extraFieldsForPage["vesselName"] = {
      label: PORT_SERVICES_FIELDS.vesselName,
      type: "text",
      minLength: 3,
    };
    extraFieldsForPage["imo"] = {
      label: PORT_SERVICES_FIELDS.imo,
      type: "integer",
      minLength: 3,
    };
  }
  return {
    ...extraFieldsForPage,
    serviceCategory: {
      label: PORT_SERVICES_FIELDS.serviceCategory,
      type: "autocomplete",
      options: portServiceCategories.map((c) => ({
        label: getPortServiceCategoryLabel(c.portServiceCategoryDesc),
        value: c.portServiceCategoryDesc,
      })),
    },
    serviceType: {
      label: PORT_SERVICES_FIELDS.serviceType,
      type: "autocomplete",
      options: portServiceTypes.map((c) => ({
        label: c.portServiceTypeDesc,
        value: c.portServiceTypeDesc,
      })),
    },
    serviceSubType: {
      label: PORT_SERVICES_FIELDS.serviceSubType,
      type: "autocomplete",
      options: portServiceSubtypes.map((c) => ({
        label: c.portServiceSubtypeDesc,
        value: c.portServiceSubtypeDesc,
      })),
    },
    status: {
      label: PORT_SERVICES_FIELDS.status,
      type: "autocomplete",
      options: getStatusFilterOptions(),
    },
    rts: {
      label: PORT_SERVICES_FIELDS.rts,
      type: "dateFromTo",
    },
    ats: {
      label: PORT_SERVICES_FIELDS.ats,
      type: "dateFromTo",
    },
    atc: {
      label: PORT_SERVICES_FIELDS.atc,
      type: "dateFromTo",
    },
  };
};
const toIsoDateOrNull = (value: string) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const appendFilterValue = (
  body: Partial<PortServicesFilterPayload>,
  field: keyof PortServicesFilterPayload,
  value: string,
) => {
  const currentValue = body[field];

  if (Array.isArray(currentValue)) {
    if (currentValue.includes(value)) {
      return;
    }

    body[field] = [...currentValue, value];
    return;
  }

  if (currentValue == null) {
    body[field] = [value];
  } else {
    body[field] = [currentValue, value];
  }
};

const setDateRangeFilter = (
  body: Partial<PortServicesFilterPayload>,
  field: "rts" | "ats" | "atc",
  value: { from?: string; to?: string },
) => {
  const nextValues: [string | null, string | null] = [
    toIsoDateOrNull(value?.from),
    toIsoDateOrNull(value?.to),
  ];
  const currentValue = body[field];

  if (
    currentValue &&
    Array.isArray(currentValue) &&
    currentValue.some((date) => date[0] === nextValues[0] && date[1] === nextValues[1])
  ) {
    return;
  }

  if (Array.isArray(currentValue)) {
    body[field] = [...currentValue, nextValues];
    return;
  }

  if (currentValue == null) {
    body[field] = [nextValues];
  }
};

export const buildFilterBodyFromUI = (
  filters: FilterItem[],
): Partial<PortServicesFilterPayload> => {
  const body: Partial<PortServicesFilterPayload> = {};

  for (const f of filters) {
    if (!f.field || f.value === "" || f.value == null) continue;

    switch (f.field) {
      case "serviceCategory":
      case "serviceType":
      case "serviceSubType":
      case "vesselCallNo":
      case "vesselName":
      case "imo":
      case "status":
        appendFilterValue(body, f.field, f.value);
        break;

      case "rts":
      case "ats":
      case "atc":
        setDateRangeFilter(body, f.field, f.value);
        break;

      default:
        break;
    }
  }

  return body;
};
